import React from "react";

const formatAddress = (address, banquet) => {
  if (typeof address === "string") return address;
  if (address && typeof address === "object") {
    return [address.line1, address.locality, address.city, address.pincode].filter(Boolean).join(", ");
  }
  return [banquet.locality, banquet.city].filter(Boolean).join(", ");
};

// Number(null) === 0, which passes Number.isFinite — so a plain Number.isFinite(Number(value))
// check never falls back for null and silently renders "0" instead of the intended fallback.
const formatNumber = (value, fallback = "-") =>
  value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? fallback : Number(value).toLocaleString("en-IN");
const formatList = (items) => (Array.isArray(items) && items.length ? items.join(", ") : "Not specified");

const policyItems = (banquet) => [
  ["Catering policy", banquet.cateringPolicy || banquet.policies?.cateringPolicy || "Contact DecorZoom for catering details"],
  ["Decor policy", banquet.decorPolicy || banquet.policies?.decorPolicy || "Contact DecorZoom for decor options"],
  ["DJ policy", banquet.djAllowed ? "DJ allowed" : "DJ not allowed"],
  ["Late night policy", banquet.lateNightAllowed ? "Late night events allowed" : "Late night events not allowed"],
  ["Power backup", banquet.powerBackup ? "Available" : "Not available"],
  ["Lift", banquet.lift ? "Available" : "Not available"],
  ["Wheelchair friendly", banquet.wheelchairFriendly ? "Yes" : "No"],
  ["Liquor policy", banquet.liquorAllowed ? (banquet.byobAllowed ? "Liquor allowed, BYOB possible" : "Liquor allowed, no BYOB") : "Liquor not allowed"],
];

const detailItems = (banquet) => [
  ["Full address", formatAddress(banquet.address, banquet)],
  ["City", banquet.city || "Not specified"],
  ["Locality", banquet.locality || "Not specified"],
  ["Guest range", banquet.guestMin || banquet.guestMax ? `${formatNumber(banquet.guestMin)} - ${formatNumber(banquet.guestMax)}` : "Not specified"],
  ["Veg price", banquet.vegPrice ? `Rs. ${formatNumber(banquet.vegPrice)} per plate` : "Not specified"],
  ["Non veg price", banquet.nonVegPrice ? `Rs. ${formatNumber(banquet.nonVegPrice)} per plate` : "Not available"],
  ["Venue type", banquet.venueType || "Not specified"],
  ["Indoor / outdoor", formatList(banquet.indoorOutdoor)],
  ["AC hall", banquet.acHall ? "Yes" : "No"],
  ["Parking", banquet.parkingAvailable ? `Yes, approx. ${banquet.parkingCapacity} cars` : "No"],
  ["Valet parking", banquet.valetParking ? "Yes" : "No"],
  ["Rooms", banquet.roomsAvailable ? `Yes, ${banquet.roomCount} rooms` : "No"],
  ["Bridal room", banquet.bridalRoom ? "Yes" : "No"],
  ["Changing room", banquet.changingRoom ? "Yes" : "No"],
];

const Card = ({ title, children }) => (
  <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[32px] sm:p-6">
    <h2 className="break-words text-xl font-semibold text-slate-900">{title}</h2>
    <div className="mt-5 min-w-0">{children}</div>
  </div>
);

export default function BanquetPolicies({ banquet }) {
  const facilities = Array.isArray(banquet.facilities) ? banquet.facilities : [];
  const goodFor = Array.isArray(banquet.goodFor) ? banquet.goodFor : [];
  const notIdealFor = Array.isArray(banquet.notIdealFor) ? banquet.notIdealFor : [];

  return (
    <div className="grid min-w-0 gap-6 xl:grid-cols-2">
      <Card title="Venue Details">
        <div className="grid gap-3">
          {detailItems(banquet).map(([label, value]) => (
            <div key={label} className="flex min-w-0 flex-col gap-1 rounded-2xl bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm font-medium text-slate-500">{label}</span>
              <span className="min-w-0 break-words text-sm font-semibold text-slate-900 sm:text-right">{value}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Policies & Access">
        <div className="grid gap-3">
          {policyItems(banquet).map(([label, value]) => (
            <div key={label} className="min-w-0 rounded-2xl bg-slate-50 px-4 py-3">
              <p className="text-sm font-medium text-slate-500">{label}</p>
              <p className="mt-1 break-words text-sm font-semibold text-slate-900">{value}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Facilities">
        <div className="flex flex-wrap gap-3">
          {facilities.length ? facilities.map((facility) => (
            <span key={facility} className="min-h-[40px] max-w-full rounded-2xl bg-slate-100 px-4 py-2 text-sm font-medium leading-5 text-slate-700 sm:rounded-full">
              {facility}
            </span>
          )) : <p className="text-sm text-slate-600">Facilities will appear here once the manager completes the listing.</p>}
        </div>
      </Card>

      <Card title="Event Fit">
        <div className="grid min-w-0 gap-4 md:grid-cols-2">
          <div className="min-w-0 rounded-3xl bg-emerald-50 p-4">
            <h3 className="break-words text-sm font-semibold uppercase tracking-[0.12em] text-emerald-700 sm:tracking-[0.2em]">Good For</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {(goodFor.length ? goodFor : ["Family celebrations", "Venue tours", "Custom decor planning"]).map((item) => (
                <li key={item} className="break-words">{item}</li>
              ))}
            </ul>
          </div>
          <div className="min-w-0 rounded-3xl bg-rose-50 p-4">
            <h3 className="break-words text-sm font-semibold uppercase tracking-[0.12em] text-rose-700 sm:tracking-[0.2em]">May Not Suit If</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {(notIdealFor.length ? notIdealFor : ["Exact restrictions pending manager confirmation"]).map((item) => (
                <li key={item} className="break-words">{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
